import { httpClient } from './http-client';
import { API_CONFIG, STORAGE_KEYS } from './api.config';
import type { ApiResponse } from './api.types';

export interface EnvioCorreoAdjuntoDto {
  id: string;
  nombreOriginal: string;
  mimeType: string;
  tamanoBytes: number;
}

export interface EnvioCorreoDto {
  id: string;
  canal: 'email' | 'whatsapp' | string;
  destinatarios: string[];
  cc: string[];
  cco: string[];
  asunto: string;
  mensajeHtml: string;
  estado: 'Enviado' | 'Error' | string;
  errorMensaje?: string | null;
  usuarioId?: string | null;
  usuarioNombre?: string | null;
  createdAt: string;
  adjuntos: EnvioCorreoAdjuntoDto[];
}

export interface EnviarCorreoPersonalizadoRequest {
  destinatarios: string[];
  cc?: string[];
  cco?: string[];
  asunto: string;
  mensajeHtml: string;
  verUrl?: string;
  incluirPdf?: boolean;
  incluirXml?: boolean;
  incluirCdr?: boolean;
}

function buildComprobantesApiUrl(endpoint: string): string {
  const empresaId = localStorage.getItem(STORAGE_KEYS.EMPRESA_ID) ?? '';
  const url = new URL(
    `${API_CONFIG.BASE_URL}/api/${API_CONFIG.API_VERSION}${endpoint}`,
    window.location.origin,
  );
  if (empresaId) url.searchParams.set('empresaId', empresaId);
  return url.toString();
}

function getComprobantesAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const empresaId = localStorage.getItem(STORAGE_KEYS.EMPRESA_ID);
  if (empresaId) headers['X-Empresa-Id'] = empresaId;
  return headers;
}

async function parseComprobantesApiResponse<T>(response: Response): Promise<ApiResponse<T>> {
  const json = await response.json();
  if (!response.ok) {
    const msg = json?.mensaje || json?.message || json?.error || `Error HTTP ${response.status}`;
    throw new Error(msg);
  }
  const data = ('exito' in json && 'data' in json) ? json.data : json;
  return { data: data as T, status: response.status, ok: true };
}

export interface ComprobanteListadoDto {
  id: string;
  serie?: string;
  correlativo?: string;
  tipoDocumentoCodigo?: string;
  tipoDocumentoNombre?: string;
  fechaEmision?: string;
  fechaVencimiento?: string;
  clienteNumeroDocumento?: string;
  clienteTipoDocumentoCodigo?: string;
  clienteNombres?: string;
  formaPagoId?: string;
  formaPagoNombre?: string;
  vendedorNombre?: string;
  monedaCodigo?: string;
  tipoCambio?: number;
  total?: number;
  estado?: string;
  correoElectronico?: string;
  direccionFacturacion?: string;
  direccionEnvio?: string;
  ordenCompra?: string;
  numeroGuia?: string;
  observacion?: string;
  observacionInterno?: string;
  centroCosto?: string;
  tipoDocumentoRelacionado?: string;
  documentoRelacionado?: string;
  tipoNotaCreditoNombre?: string;
  tipoNotaDebitoNombre?: string;
  motivoEmisionNc?: string;
  motivoEmisionNd?: string;
  codigoRespuesta?: string;
  mensajeRespuesta?: string;
  codigoQr?: string;
  createdAt?: string;
  updatedAt?: string;
  tieneMovimientoStock?: boolean;
  tieneNotaSalidaActiva?: boolean;
  origenVenta?: string;
}

export interface ComprobanteItemDto {
  id: string;
  comprobanteId: string;
  productoId?: string;
  productoCodigo?: string;
  productoNombre?: string;
  codigoSunat?: string;
  cantidad: number;
  valorVentaUnitario?: number;
  valorVentaNeto?: number;
  precioVentaUnitario?: number;
  precioVentaNeto?: number;
  valorVentaTotal?: number;
  precioVentaTotal?: number;
  descuentoFactor?: number;
  igv?: number;
  descuento?: number;
  unidadMedidaId?: string;
  unidadMedidaNombre?: string;
  unidadMedidaCodigoSunat?: string;
  afectacionTributariaId?: string;
  afectacionTributariaCodigo?: string;
  afectacionTributariaCodigoTributo?: string;
  afectacionTributariaImpuesto?: string;
  afectacionTributariaNombre?: string;
  categoriaId?: string;
  categoriaNombre?: string;
  descripcion?: string;
  esDetraccion?: boolean;
  detraccionPorcentaje?: number;
  detraccionCodigo?: string;
}

export interface ComprobanteCuotaDto {
  id: string;
  numeroCuota: number;
  importe: number;
  porcentaje?: number;
  diasCredito?: number;
  fechaVencimiento?: string;
  estado: string;
}

export interface ComprobanteCuotaInputDto {
  numeroCuota: number;
  importe: number;
  porcentaje?: number;
  diasCredito?: number;
  fechaVencimiento?: string;
}

export interface ComprobanteHistorialDto {
  id: string;
  estado?: string;
  codigoRespuesta?: string;
  mensajeRespuesta?: string;
  descripcion?: string;
  usuarioNombre?: string;
  createdAt?: string;
}

export interface ComprobanteDto extends ComprobanteListadoDto {
  establecimientoId?: string;
  clienteId?: string;
  formaPagoId?: string;
  tipoCambio?: number;
  montoImponible?: number;
  igv?: number;
  exonerado?: number;
  inafecto?: number;
  exportacion?: number;
  descuentoTotal?: number;
  descuentoItems?: number;
  descuentoItemsSinIgv?: number;
  esDetraccion?: boolean;
  tipoOperacionId?: string;
  tipoOperacionCodigo?: string;
  tipoOperacionNombre?: string;
  detraccionMedioPago?: string;
  detraccionMedioPagoNombre?: string;
  detraccionCodigoAplicada?: string;
  detraccionNombreAplicada?: string;
  detraccionPorcentaje?: number;
  detraccionMontoRedondeado?: number;
  detraccionNetoCobrar?: number;
  detraccionResponsableDeposito?: 'cliente' | 'empresa';
  esRetencion?: boolean;
  retencionPorcentaje?: number;
  retencionImporteBase?: number;
  retencionMonto?: number;
  retencionNetoCobrar?: number;
  retencionNumeroSerieRecibido?: string;
  retencionFechaRecibido?: string;
  retencionEstadoConciliacion?: string;
  tipoNotaCreditoCodigo?: string;
  tipoNotaCreditoNombre?: string;
  tipoNotaDebitoCodigo?: string;
  tipoNotaDebitoNombre?: string;
  motivoEmisionNd?: string;
  motivoEmisionNc?: string;
  tieneGuiaRemitenteActiva?: boolean;
  usuarioId?: string;
  usuarioNombre?: string;
  comprobanteItems: ComprobanteItemDto[];
  cuotas: ComprobanteCuotaDto[];
  historial?: ComprobanteHistorialDto[];
}

export interface ComprobanteItemInputDto {
  productoId?: string;
  productoCodigo?: string;
  productoNombre?: string;
  codigoSunat?: string;
  cantidad: number;
  precioVentaUnitario?: number;
  precioVentaNeto?: number;
  valorVentaUnitario?: number;
  valorVentaNeto?: number;
  igv?: number;
  igvTotal?: number;
  descuento?: number;
  descuentoFactor?: number;
  tipoDescuentoFormato?: string;
  valorVentaDescontado?: number;
  precioVentaDescontado?: number;
  valorVentaTotal?: number;
  precioVentaTotal?: number;
  unidadMedidaId?: string;
  unidadMedidaNombre?: string;
  unidadMedidaCodigoSunat?: string;
  afectacionTributariaId?: string;
  afectacionTributariaCodigo?: string;
  afectacionTributariaCodigoTributo?: string;
  afectacionTributariaImpuesto?: string;
  afectacionTributariaNombre?: string;
  categoriaId?: string;
  categoriaNombre?: string;
  descripcion?: string;
  esDetraccion?: boolean;
  detraccionPorcentaje?: number;
  detraccionCodigo?: string;
}

export interface ComprobanteInputDto {
  empresaId?: string;
  establecimientoId?: string;
  serie?: string;
  correlativo?: string;
  tipoDocumentoCodigo?: string;
  tipoDocumentoNombre?: string;
  fechaEmision?: string;
  fechaVencimiento?: string;
  clienteId?: string;
  clienteNumeroDocumento?: string;
  clienteTipoDocumentoCodigo?: string;
  clienteNombres?: string;
  formaPagoId?: string;
  formaPagoNombre?: string;
  cuotas?: ComprobanteCuotaInputDto[];
  vendedorId?: string;
  vendedorNombre?: string;
  ordenCompra?: string;
  numeroGuia?: string;
  ordenVentaOrigenId?: string;
  ordenVentaOrigenNumero?: string;
  notaVentaOrigenId?: string;
  notaVentaOrigenNumero?: string;
  cotizacionOrigenId?: string;
  cotizacionOrigenNumero?: string;
  guiaOrigenId?: string;
  guiaOrigenNumero?: string;
  monedaCodigo?: string;
  monedaNombre?: string;
  tipoCambio?: number;
  observacion?: string;
  observacionInterno?: string;
  montoImponible?: number;
  exonerado?: number;
  inafecto?: number;
  exportacion?: number;
  descuentoTotal?: number;
  tipoDescuentoGlobal?: string;
  valorDescuentoGlobal?: number;
  descuentoItems?: number;
  descuentoItemsSinIgv?: number;
  igv?: number;
  total?: number;
  direccionFacturacion?: string;
  correoElectronico?: string;
  direccionEnvio?: string;
  centroCosto?: string;
  tipoNotaCreditoCodigo?: string;
  tipoNotaCreditoNombre?: string;
  tipoNotaDebitoCodigo?: string;
  tipoNotaDebitoNombre?: string;
  motivoEmisionNd?: string;
  motivoEmisionNc?: string;
  tipoDocumentoRelacionado?: string;
  documentoRelacionado?: string;
  estado?: string;
  codigoRespuesta?: string;
  mensajeRespuesta?: string;
  motivoAnulacion?: string;
  esDetraccion?: boolean;
  tipoOperacionId?: string;
  tipoOperacionCodigo?: string;
  tipoOperacionNombre?: string;
  detraccionMedioPago?: string;
  detraccionMedioPagoNombre?: string;
  detraccionCodigoAplicada?: string;
  detraccionNombreAplicada?: string;
  detraccionPorcentaje?: number;
  detraccionMontoRedondeado?: number;
  detraccionNetoCobrar?: number;
  detraccionResponsableDeposito?: 'cliente' | 'empresa';
  esRetencion?: boolean;
  retencionPorcentaje?: number;
  retencionImporteBase?: number;
  retencionMonto?: number;
  retencionNetoCobrar?: number;
  retencionNumeroSerieRecibido?: string;
  retencionFechaRecibido?: string;
  retencionEstadoConciliacion?: string;
  origenVenta?: string;
  motivoTraslado?: string;
  modalidadTransporte?: string;
  pesoTotal?: number;
  unidadPeso?: string;
  indicadorTrasladoTotalBienes?: boolean;
  puntoPartidaDireccion?: string;
  puntoPartidaUbigeo?: string;
  puntoPartidaDepartamento?: string;
  puntoPartidaProvincia?: string;
  puntoPartidaDistrito?: string;
  puntoLlegadaDireccion?: string;
  puntoLlegadaUbigeo?: string;
  puntoLlegadaDepartamento?: string;
  puntoLlegadaProvincia?: string;
  puntoLlegadaDistrito?: string;
  transportistaNombre?: string;
  transportistaNumeroDocumento?: string;
  transportistaTipoDocumento?: string;
  transportistaRegistroMtc?: string;
  numeroRegistroMtc?: string;
  proveedorNombre?: string;
  proveedorNumeroDocumento?: string;
  proveedorTipoDocumento?: string;
  compradorNombre?: string;
  compradorNumeroDocumento?: string;
  compradorTipoDocumento?: string;
  remitenteNombre?: string;
  remitenteNumeroDocumento?: string;
  remitenteTipoDocumento?: string;
  transporteSubcontratado?: boolean;
  subcontratadorNombre?: string;
  subcontratadorNumeroDocumento?: string;
  subcontratadorTipoDocumento?: string;
  pagadorFlete?: string;
  pagadorTerceroNombre?: string;
  pagadorTerceroNumeroDocumento?: string;
  pagadorTerceroTipoDocumento?: string;
  items: ComprobanteItemInputDto[];
}

export interface DocumentoRelacionadoDto {
  id: string;
  serie?: string;
  correlativo?: string;
  tipoDocumentoCodigo?: string;
  tipoDocumentoNombre?: string;
  estado?: string;
  total?: number;
  fechaEmision?: string;
  tipoNotaCreditoNombre?: string;
  tipoNotaDebitoNombre?: string;
  motivoEmisionNc?: string;
  motivoEmisionNd?: string;
  relacionTipo: 'original' | 'nota_credito' | 'nota_debito' | 'relacionado' | 'orden_venta' | 'nota_venta' | 'nota_salida' | 'comprobante';
}

export const comprobantesService = {
  getAll: (params?: {
    establecimientoId?: string;
    clienteId?: string;
    page?: number;
    pageSize?: number;
    search?: string;
    estado?: string;
    fechaDesde?: string;
    fechaHasta?: string;
    tipoDocumento?: string;
    vendedor?: string;
    formaPago?: string;
    totalMin?: number;
    totalMax?: number;
  }): Promise<ApiResponse<ComprobanteListadoDto[]>> =>
    httpClient.get('/comprobantes', params as Record<string, string | number | boolean | undefined>),

  getById: async (id: string): Promise<ApiResponse<ComprobanteDto>> => {
    type RawDto = ComprobanteListadoDto & {
      establecimientoId?: string;
      clienteId?: string;
      formaPagoId?: string;
      tipoCambio?: number;
      tipoNotaCreditoCodigo?: string;
      tipoNotaDebitoCodigo?: string;
      items?: ComprobanteItemDto[];
      cuotas?: ComprobanteCuotaDto[];
    };
    const res = await httpClient.get<RawDto>(`/comprobantes/${id}`);
    if (!res.ok || !res.data) return res as unknown as ApiResponse<ComprobanteDto>;
    const { items, cuotas, ...rest } = res.data;
    return { ...res, data: { ...rest, comprobanteItems: items ?? [], cuotas: cuotas ?? [] } };
  },

  create: (data: ComprobanteInputDto): Promise<ApiResponse<ComprobanteListadoDto>> =>
    httpClient.post('/comprobantes', data),

  update: (id: string, data: ComprobanteInputDto): Promise<ApiResponse<ComprobanteDto>> =>
    httpClient.put(`/comprobantes/${id}`, data),

  anular: (id: string, motivo: string): Promise<ApiResponse<unknown>> =>
    httpClient.patch(`/comprobantes/${id}/anular`, { motivo }),

  validarAnulacion: (id: string): Promise<ApiResponse<{ tieneNotaSalidaActiva: boolean }>> =>
    httpClient.get(`/comprobantes/${id}/validar-anulacion`),

  downloadPdf: (id: string): Promise<Blob> =>
    httpClient.obtenerBlob(`/comprobantes/${id}/pdf`, { disposition: 'attachment' }),

  downloadTicket: (id: string, opts?: { anchoMm?: number; cajaNombre?: string; cajeroNombre?: string; cajaApertura?: string }): Promise<Blob> =>
    httpClient.obtenerBlob(`/comprobantes/${id}/ticket`, { disposition: 'attachment', ...opts }),

  downloadPdfBulk: (ids: string[], output: 'combined' | 'zip'): Promise<Blob> =>
    httpClient.obtenerBlob('/comprobantes/pdf/bulk', { ids: ids.join(','), output }),

  delete: (id: string): Promise<ApiResponse<unknown>> =>
    httpClient.delete(`/comprobantes/${id}`),

  emitirBorrador: (id: string): Promise<ApiResponse<ComprobanteDto>> =>
    httpClient.patch(`/comprobantes/${id}/emitir`, {}),

  reenviar: (id: string): Promise<ApiResponse<unknown>> =>
    httpClient.post(`/comprobantes/${id}/reenviar`, {}),

  enviarCorreo: (id: string, email: string, verUrl?: string, empresaId?: string): Promise<ApiResponse<unknown>> =>
    httpClient.post(`/comprobantes/${id}/enviar-correo`, { email, verUrl }, empresaId ? { empresaId } : undefined),

  registrarEnvioWhatsapp: (id: string, telefono: string, mensaje: string): Promise<ApiResponse<unknown>> =>
    httpClient.post(`/comprobantes/${id}/registrar-envio-whatsapp`, { telefono, mensaje }),

  enviarCorreoPersonalizado: async (
    id: string,
    datos: EnviarCorreoPersonalizadoRequest,
    adjuntos: File[] = [],
  ): Promise<ApiResponse<EnvioCorreoDto>> => {
    const formData = new FormData();
    formData.append('datos', JSON.stringify(datos));
    adjuntos.forEach((file) => formData.append('adjuntos', file, file.name));
    const response = await fetch(buildComprobantesApiUrl(`/comprobantes/${id}/enviar-correo-personalizado`), {
      method: 'POST',
      headers: getComprobantesAuthHeaders(),
      body: formData,
    });
    return parseComprobantesApiResponse<EnvioCorreoDto>(response);
  },

  historialCorreos: (id: string): Promise<ApiResponse<EnvioCorreoDto[]>> =>
    httpClient.get(`/comprobantes/${id}/historial-correos`),

  getRelacionados: (id: string): Promise<ApiResponse<DocumentoRelacionadoDto[]>> =>
    httpClient.get(`/comprobantes/${id}/relacionados`),

  downloadExcel: (params?: {
    empresaId?: string;
    establecimientoId?: string;
    search?: string;
    estado?: string;
    fechaDesde?: string;
    fechaHasta?: string;
    tipoDocumento?: string;
    vendedor?: string;
    formaPago?: string;
    totalMin?: number;
    totalMax?: number;
    ids?: string[];
  }): Promise<Blob> => {
    const { ids, ...rest } = params ?? {};
    const query: Record<string, string | number | boolean | undefined> = rest as Record<string, string | number | boolean | undefined>;
    if (ids && ids.length > 0) query.ids = ids.join(',');
    return httpClient.obtenerBlob('/reportes/comprobantes/excel', query);
  },

  downloadRegistroVentasContasiscorpSql: (params: {
    empresaId?: string;
    fechaDesde: string;
    fechaHasta: string;
  }): Promise<Blob> =>
    httpClient.obtenerBlob('/reportes/registro-ventas/contasiscorp-sql', params),
};
